import { ReactNode } from 'react'

import urls from '../../routers/urls'
import {
  RiApps2Line,
  RiBookmark3Line,
  RiCalendarTodoLine,
  RiHistoryLine,
  RiLogoutBoxRLine,
  RiWalletLine,
  RiTimeLine,
} from 'react-icons/ri'
import { PermissionHelper } from '../helpers'
import { TenantAtomType } from '../../recoil/atoms'

export enum SideBarQueries {
  TRIPS = 'latestStatus=WAITING_FOR_CARRIER_APPROVAL',
  ACTIVE_TRIP_LEGS = 'tripLegStatus=WAITING&tripLegStatus=PICKUP_IN_PROGRESS&tripLegStatus=IN_VEHICLE&tripLegStatus=DROPOFF_IN_PROGRESS',
  DELIVERED_TRIP_LEGS = 'tripLegStatus=DELIVERED',
}

export type MenuValidationsType = Record<string, string[]>

export type MainNavigationItemType = {
  disabled?: boolean
  icon?: ReactNode
  path?: string
  text?: string
  name?: string
  showTitle?: boolean
}

export type MainNavigationType = MainNavigationItemType[]

export type SideBarDataItemType = {
  icon?: ReactNode
  onClick?: (arg: any) => void
  path?: string
  permissions?: {
    countries?: string[]
    flags?: string[]
  }
  text?: string
  type?: string
  view?: ReactNode
}

export type SideBarDataType = SideBarDataItemType[]

export type SideBarsType = Record<string, SideBarDataType>

export enum NAVIGATION_KEY {
  MAIN = 'MAIN',
  HEADER_MENU_DATA = 'HEADER_MENU_DATA',
}

export const MAIN = [
  {
    icon: <RiBookmark3Line size={20} />,
    path: urls.Loads,
    text: 'tabs.loads',
    name: 'tabs.loads',
  },
  {
    icon: <RiApps2Line size={20} />,
    path: urls.CarrierHome,
    text: 'tabs.myWorks',
    name: 'tabs.myWorks',
  },
  {
    icon: <RiWalletLine size={20} />,
    path: urls.Invoices,
    text: 'tabs.myPayments',
    name: 'tabs.myPayments',
    showTitle: false,
  },
]

export const HEADER_MENU_DATA = [
  {
    text: 'account.signOut',
    path: urls.Authentication,
    icon: <RiLogoutBoxRLine size={20} />,
  },
]

export const SIDEBARS: SideBarsType = {
  [urls.CarrierHome]: [
    {
      icon: <RiTimeLine size={20} />,
      path: urls.CarrierHome,
      permissions: {
        countries: ['DE'],
      },
      text: 'myWorks.tabs.waiting_approve',
      type: 'trips',
    },
    {
      icon: <RiApps2Line size={20} />,
      path: urls.CarrierHome,
      text: 'myWorks.tabs.active',
      type: 'active-trip-legs',
    },
    {
      icon: <RiCalendarTodoLine size={20} />,
      path: urls.CarrierHome,
      text: 'myWorks.tabs.past',
      type: 'delivered-trip-legs',
    },
  ],
}

type NavigationType = Record<string, any>

export const NAVIGATIONS: NavigationType = {
  MAIN,
  HEADER_MENU_DATA,
}

const createPermittedData = (key: string, enabledFeatures: string[], userRole: string) => {
  return NAVIGATIONS[key].filter((item: { validations: MenuValidationsType }) => {
    if (!item.validations) {
      return item
    }

    const permissionIsValid =
      item.validations && PermissionHelper.check(enabledFeatures, userRole, item?.validations)

    if (permissionIsValid) {
      return item
    }
  })
}

export const getNavigation = (
  key: string,
  t: (arg: string) => void,
  enabledFeatures: string[],
  userRole: string
) => {
  if (!(key in NAVIGATIONS)) {
    return []
  }

  const permittedData = createPermittedData(key, enabledFeatures, userRole)

  return permittedData.map((item: { path?: string; text?: string }) => ({
    ...item,
    text: t(item.text || ''),
  }))
}

export const getSideBarData = (key: string, t: (arg: string) => string) => {
  if (!(key in SIDEBARS)) {
    return []
  }

  const result = SIDEBARS[key].map((item: SideBarDataItemType) => ({
    ...item,
    text: t(item.text || ''),
  }))

  return result
}

export const getPermittedSideBarData = (result: SideBarDataItemType[], tenant: TenantAtomType) => {
  const permittedResult = result?.filter?.((item: SideBarDataItemType) => {
    if (!item.permissions) {
      return item
    }

    const permissionIsValid = PermissionHelper.checkSideBar(
      [tenant?.onboarding?.country || ''],
      item?.permissions
    )

    if (permissionIsValid) {
      return item
    }
  })

  return permittedResult
}

export const getSubMenus = (t: (arg: string) => string, tenant: TenantAtomType) => {
  return Object.keys(SIDEBARS).reduce((acc, key) => {
    const obj: Record<any, any> = acc
    const arr = SIDEBARS[key]?.map(item => ({ ...item, text: t(item.text || '') }))
    const permittedArr = getPermittedSideBarData(arr, tenant)
    obj[key] = permittedArr

    return obj
  }, {})
}
